import { useState } from 'react'
import {
  Card, Row, Col, Form, DatePicker, Select, Button, Table, Tabs,
  Statistic, Tag, Typography, Space, Progress,
} from 'antd'
import {
  DownloadOutlined, SearchOutlined, ThunderboltOutlined,
  AlertOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { reportsApi } from '@/features/reports/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const { RangePicker } = DatePicker

const DEPARTMENTS = [
  'BLOWROOM', 'CARDING', 'BREAKER_DRAW', 'FINISHER_DRAW',
  'COMBING', 'ROVING', 'RING_SPINNING', 'AUTO_WINDING', 'TFO',
]

const WASTE_TYPES = {
  HW: 'Hard Waste', SW: 'Soft Waste', PF: 'Pneumafil',
  NO: 'Noil', RW: 'Rework Waste', FL: 'Fly/Cleaning', BR: 'Blowroom Droppings',
}

const BD_CATEGORIES = {
  M: 'Mechanical', E: 'Electrical', Q: 'Quality Hold',
  P: 'Material Pending', U: 'Utility', O: 'Operator Absent',
  C: 'Cleaning', S: 'Setup/Changeover',
}

export default function ProductionReportsPage() {
  const { plantId } = usePlantId()

  const [form] = Form.useForm()
  const [params, setParams] = useState({
    plant_id: plantId,
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().format('YYYY-MM-DD'),
    department_code: undefined as string | undefined,
  })

  const { data: kpi } = useQuery({
    queryKey: ['production-kpi', params],
    queryFn: () => reportsApi.productionKpi(params),
    enabled: !!params.plant_id,
  })

  const { data: prodSummary, isLoading: prodLoading } = useQuery({
    queryKey: ['production-summary', params],
    queryFn: () => reportsApi.productionSummary(params),
    enabled: !!params.plant_id,
  })

  const { data: wasteRows, isLoading: wasteLoading } = useQuery({
    queryKey: ['waste-tracking', params],
    queryFn: () => reportsApi.wasteTracking(params),
    enabled: !!params.plant_id,
  })

  const { data: wasteSummary } = useQuery({
    queryKey: ['waste-summary', params],
    queryFn: () => reportsApi.wasteSummary(params),
    enabled: !!params.plant_id,
  })

  const { data: breakdowns, isLoading: bdLoading } = useQuery({
    queryKey: ['machine-breakdown', params],
    queryFn: () => reportsApi.machineBreakdown(params),
    enabled: !!params.plant_id,
  })

  const { data: mttrMtbf } = useQuery({
    queryKey: ['mttr-mtbf', params],
    queryFn: () => reportsApi.mttrMtbf(params),
    enabled: !!params.plant_id,
  })

  const handleSearch = () => {
    const vals = form.getFieldsValue()
    const [df, dt] = vals.dateRange || []
    setParams(prev => ({
      ...prev,
      date_from: df ? df.format('YYYY-MM-DD') : prev.date_from,
      date_to: dt ? dt.format('YYYY-MM-DD') : prev.date_to,
      department_code: vals.department_code,
    }))
  }

  const prodColumns = [
    { title: 'Log No', dataIndex: 'log_no', key: 'log_no', width: 110 },
    { title: 'Date', dataIndex: 'log_date', key: 'date', width: 110 },
    { title: 'Department', dataIndex: 'department', key: 'dept', width: 140 },
    { title: 'Shift', dataIndex: 'shift', key: 'shift', width: 80 },
    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count', width: 90, align: 'center' as const },
    { title: 'Planned (kg)', dataIndex: 'planned_kg', key: 'planned', align: 'right' as const, width: 110 },
    { title: 'Actual (kg)', dataIndex: 'actual_kg', key: 'actual', align: 'right' as const, width: 110 },
    {
      title: 'Achievement %', dataIndex: 'achievement_pct', key: 'achieve', align: 'right' as const, width: 120,
      render: (v: string) => {
        const n = parseFloat(v)
        return <span style={{ color: n >= 95 ? '#389e0d' : n >= 85 ? '#d46b08' : '#cf1322', fontWeight: 600 }}>{v}%</span>
      }
    },
    { title: 'Spindle Util %', dataIndex: 'spindle_util_pct', key: 'spindle', align: 'right' as const, width: 120 },
    { title: 'Stoppage (min)', dataIndex: 'total_stoppage_min', key: 'stop', align: 'right' as const, width: 120 },
    { title: 'Waste %', dataIndex: 'waste_pct', key: 'waste', align: 'right' as const, width: 90 },
    { title: 'Power/kg', dataIndex: 'power_per_kg', key: 'power', align: 'right' as const, width: 90 },
    {
      title: 'Verified', dataIndex: 'verified', key: 'verified', width: 80,
      render: (v: string) => <Tag color={v === 'Yes' ? 'green' : 'default'}>{v}</Tag>
    },
  ]

  const wasteColumns = [
    { title: 'Record No', dataIndex: 'record_no', key: 'no', width: 120 },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 110 },
    { title: 'Department', dataIndex: 'department', key: 'dept', width: 140 },
    { title: 'Shift', dataIndex: 'shift', key: 'shift', width: 80 },
    { title: 'Waste Type', dataIndex: 'waste_type', key: 'type', width: 130 },
    { title: 'Weight (kg)', dataIndex: 'weight_kg', key: 'wt', align: 'right' as const, width: 110 },
    { title: 'Input (kg)', dataIndex: 'input_kg', key: 'input', align: 'right' as const, width: 110 },
    { title: 'Waste %', dataIndex: 'waste_pct', key: 'pct', align: 'right' as const, width: 90 },
    { title: 'Standard %', dataIndex: 'standard_pct', key: 'std', align: 'right' as const, width: 90 },
    {
      title: 'Variance %', dataIndex: 'variance_pct', key: 'var', align: 'right' as const, width: 100,
      render: (v: string) => {
        const n = parseFloat(v)
        return <span style={{ color: n > 0 ? '#cf1322' : n < 0 ? '#389e0d' : 'inherit' }}>{v}%</span>
      }
    },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true },
  ]

  const bdColumns = [
    { title: 'BD No', dataIndex: 'breakdown_no', key: 'no', width: 110 },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 110 },
    { title: 'Machine', dataIndex: 'machine', key: 'machine', width: 160, ellipsis: true },
    {
      title: 'Category', dataIndex: 'category', key: 'cat', width: 120,
      render: (v: string) => {
        const colors: Record<string, string> = {
          Mechanical: 'red', Electrical: 'orange', 'Quality Hold': 'gold',
          'Material Pending': 'blue', Utility: 'purple', 'Operator Absent': 'cyan',
          Cleaning: 'default', 'Setup/Changeover': 'geekblue',
        }
        return <Tag color={colors[v] || 'default'}>{v}</Tag>
      }
    },
    { title: 'BD Time', dataIndex: 'breakdown_time', key: 'bdt', width: 90 },
    { title: 'Restore Time', dataIndex: 'restoration_time', key: 'rst', width: 100 },
    { title: 'Duration (min)', dataIndex: 'duration_min', key: 'dur', align: 'right' as const, width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'desc', ellipsis: true, width: 200 },
    { title: 'Root Cause', dataIndex: 'root_cause', key: 'rc', ellipsis: true, width: 160 },
    {
      title: 'Repeat', dataIndex: 'is_repeat', key: 'repeat', width: 80,
      render: (v: string) => <Tag color={v === 'Yes' ? 'red' : 'default'}>{v}</Tag>
    },
    {
      title: 'CAPA', dataIndex: 'capa_raised', key: 'capa', width: 80,
      render: (v: string) => <Tag color={v === 'Yes' ? 'green' : 'default'}>{v}</Tag>
    },
  ]

  const mttrColumns = [
    { title: 'Machine', dataIndex: 'machine', key: 'machine', width: 200 },
    { title: 'Total Breakdowns', dataIndex: 'total_breakdowns', key: 'total', align: 'right' as const },
    { title: 'Repeat BDs', dataIndex: 'repeat_breakdowns', key: 'repeat', align: 'right' as const,
      render: (v: number) => <span style={{ color: v > 0 ? '#cf1322' : 'inherit' }}>{v}</span> },
    { title: 'Total BD (min)', dataIndex: 'total_breakdown_min', key: 'total_min', align: 'right' as const },
    { title: 'MTTR (min)', dataIndex: 'mttr_min', key: 'mttr', align: 'right' as const,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: 'MTBF (min)', dataIndex: 'mtbf_min', key: 'mtbf', align: 'right' as const },
    {
      title: 'Availability %', dataIndex: 'availability_pct', key: 'avail', align: 'right' as const,
      render: (v: string) => {
        const n = parseFloat(v)
        return (
          <span style={{ color: n >= 95 ? '#389e0d' : n >= 90 ? '#d46b08' : '#cf1322', fontWeight: 600 }}>
            {v}%
          </span>
        )
      }
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ThunderboltOutlined style={{ marginRight: 8, color: '#faad14' }} />
        Production & Operations Reports
      </Title>
      <Typography.Text type="secondary">Shift production, waste tracking, machine breakdown, MTTR/MTBF</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="dateRange" label="Period">
            <RangePicker defaultValue={[dayjs().startOf('month'), dayjs()]} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="department_code" label="Department">
            <Select allowClear style={{ width: 160 }} placeholder="All">
              {DEPARTMENTS.map(d => (
                <Select.Option key={d} value={d}>{d.replace('_', ' ')}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Apply</Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.productionSummaryExportUrl(params)} download>
                Export Production
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.wasteTrackingExportUrl(params)} download>
                Export Waste
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.machineBreakdownExportUrl(params)} download>
                Export Breakdown
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.mttrMtbfExportUrl(params)} download>
                Export MTTR/MTBF
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* KPI Cards */}
      {kpi && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={4}>
            <Card>
              <Statistic title="Total Planned (kg)" value={kpi.total_planned_kg} precision={3} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Total Actual (kg)" value={kpi.total_actual_kg} precision={3} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Plan Achievement"
                value={kpi.plan_achievement_pct}
                precision={2}
                suffix="%"
                valueStyle={{ color: kpi.plan_achievement_pct >= 95 ? '#389e0d' : '#d46b08' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Avg Spindle Utilization"
                value={kpi.avg_spindle_utilization}
                precision={2}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Avg Machine Efficiency"
                value={kpi.avg_machine_efficiency}
                precision={2}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Total Stoppage (min)"
                value={kpi.total_stoppage_minutes}
                valueStyle={{ color: '#cf1322' }}
                prefix={<AlertOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Total Waste (kg)" value={kpi.total_waste_kg} precision={3} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Avg Waste %" value={kpi.avg_waste_pct} precision={4} suffix="%" />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Total Power (kWh)" value={kpi.total_power_kwh} precision={3} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Avg Power/kg" value={kpi.avg_power_per_kg} precision={4} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Quality Rejections (kg)" value={kpi.total_quality_rej_kg} precision={3} valueStyle={{ color: '#cf1322' }} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="Records" value={kpi.records} />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs
        defaultActiveKey="production"
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'production',
            label: 'Shift Production',
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />} href={reportsApi.productionSummaryExportUrl(params)} download>
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(prodSummary) ? prodSummary : []}
                  columns={prodColumns}
                  rowKey="log_no"
                  loading={prodLoading}
                  scroll={{ x: 1800 }}
                  size="small"
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: 'waste',
            label: 'Waste Tracking',
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />} href={reportsApi.wasteTrackingExportUrl(params)} download>
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(wasteRows) ? wasteRows : []}
                  columns={wasteColumns}
                  rowKey="record_no"
                  loading={wasteLoading}
                  scroll={{ x: 1200 }}
                  size="small"
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: 'waste-summary',
            label: 'Waste by Stage',
            children: (
              <Card>
                <Table
                  dataSource={Array.isArray(wasteSummary) ? wasteSummary : []}
                  columns={[
                    { title: 'Department', dataIndex: 'department', key: 'dept', width: 160 },
                    { title: 'Waste Type', dataIndex: 'waste_type', key: 'type', width: 140 },
                    { title: 'Total (kg)', dataIndex: 'total_kg', key: 'total', align: 'right' as const },
                    { title: 'Avg Waste %', dataIndex: 'avg_waste_pct', key: 'avg', align: 'right' as const },
                    { title: 'Std %', dataIndex: 'std_waste_pct', key: 'std', align: 'right' as const },
                    {
                      title: 'Variance %', dataIndex: 'variance_pct', key: 'var', align: 'right' as const,
                      render: (v: string) => {
                        const n = parseFloat(v)
                        return <span style={{ color: n > 0 ? '#cf1322' : n < 0 ? '#389e0d' : 'inherit', fontWeight: 600 }}>{v}%</span>
                      }
                    },
                    { title: 'Records', dataIndex: 'records', key: 'records', align: 'right' as const },
                  ]}
                  rowKey={(r) => `${r.department}-${r.waste_type}`}
                  size="small"
                  pagination={false}
                />
              </Card>
            ),
          },
          {
            key: 'breakdown',
            label: 'Machine Breakdown',
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />} href={reportsApi.machineBreakdownExportUrl(params)} download>
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(breakdowns) ? breakdowns : []}
                  columns={bdColumns}
                  rowKey="breakdown_no"
                  loading={bdLoading}
                  scroll={{ x: 1400 }}
                  size="small"
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: 'mttr-mtbf',
            label: 'MTTR / MTBF',
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />} href={reportsApi.mttrMtbfExportUrl(params)} download>
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(mttrMtbf) ? mttrMtbf : []}
                  columns={mttrColumns}
                  rowKey="machine"
                  size="small"
                  pagination={false}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  )
}
